/* Importing the necessary libraries and files */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import PropTypes from 'prop-types';

/* Importing the components from the Material UI library. */
import { Box, Card, CardContent, Grid, Tab } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';

/* Importing the getPatient function from the patientSlice.js file. */
import { getPatient } from '../../features/patient/patientSlice';

/* Importing the components. */
import Telemetry from '../../components/Telemetry';
import EncounterList from '../Encounter/Encounter.section.list';
import PatientSectionDetails from './Patient.section.details';
import PatientSectionRemarks from './Patient.section.remarks';
import PatientSectionMonitoringCharts from './Patient.section.monitoringCharts';
import PatientSectionParameterSettings from './Patient.section.parameterSettings';

/* Importing functions from the utils folder. */
import fetchAndHandleData from '../../utils/fetchAndHandleData';
import { fPatient } from '../../utils/Patient/Patient.utils.format';

PatientTabs.propTypes = {
  patientID: PropTypes.string,
  call: PropTypes.bool,
};

/* Exporting the PatientRegister function. */
export default function PatientTabs(props) {
  const { patientID, call } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { Patients } = useSelector((state) => state.patient);
  const currentPatient = Patients.find((el) => el.id === patientID);

  const [searchParams, setSearchParams] = useSearchParams();

  /* Setting the initial state of the pagePatient object. */
  const [pagePatient, setPagePatient] = useState({
    name_given: '',
    name_family: '',
    name_suffix: '',
    gender: '',
    birthDate: '',
    address_line: '',
    address_city: '',
    address_postalCode: '',
    address_country: '',
    photo_url: '',
    deceasedBoolean: false,
    maritalStatus_code: '',
    telecom_phone_value: '',
    telecom_email_value: '',
    contact_name_text: '',
    contact_relationship_text: '',
    contact_telecom_value: '',
    'philhealthNo-id': '',
    'SSSNo-id': '',
    'GSISNo-id': '',
    'patient-pregnancyStatus': false,
    'patient-birthPlace': '',
    'patient-hmo': '',
    'patient-mothersMaidenName': '',
    'patient-nationality': '',
    'patient-remarks': '',
    'patient-lifeline-config-ecg_st_msec': '',
    'patient-lifeline-config-heartrate_upper_bpm': '',
    'patient-lifeline-config-heartrate_lower_bpm': '',
    'patient-lifeline-config-pulserate_upper_bpm': '',
    'patient-lifeline-config-pulserate_lower_bpm': '',
    'patient-lifeline-config-oxygen_upper_saturation': '',
    'patient-lifeline-config-oxygen_lower_saturation': '',
    'patient-lifeline-config-respiratory_upper_rpm': '',
    'patient-lifeline-config-respiratory_lower_rpm': '',
    'patient-lifeline-config-bp_systolic_upper': '',
    'patient-lifeline-config-bp_systolic_lower': '',
    'patient-lifeline-config-bp_diastolic_upper': '',
    'patient-lifeline-config-bp_diastolic_lower': '',
    'patient-lifeline-config-time_frame': '',
    'patient-lifeline-config-temperature_upper': '',
    'patient-lifeline-config-temperature_lower': '',
  });

  const [value, setValue] = useState('info');

  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (newValue !== 'info') {
      setSearchParams({ tab: newValue });
    } else {
      setSearchParams({});
    }
  };

  useEffect(() => {
    if (!currentPatient) return;
    setPagePatient(fPatient(currentPatient));
  }, [currentPatient]);

  const initializeData = useCallback(async () => {
    if (patientID) {
      await fetchAndHandleData({
        dispatch,
        navigate,
        fetchAction: getPatient,
        id: patientID,
        errorMessage: 'No Patient record found',
        navigateOnFailure: true,
        navigateTo: '/patient/list',
      });
      if (searchParams.get('tab')) {
        setValue(searchParams.get('tab'));
      }
    }
  }, [dispatch, navigate, patientID, searchParams]);

  /* Calling the initializeData function when the component mounts. */
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  return (
    <Grid container>
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ width: '100%', typography: 'body1' }}>
              <TabContext value={value}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <TabList onChange={handleChange} aria-label="lab API tabs example" variant="scrollable">
                    <Tab label="Personal Information" value="info" />
                    <Tab label="Encounters" value="encounters" />
                    <Tab label="Remarks" value="remarks" />
                    <Tab label="Monitoring Charts" value="monitoring-charts" />
                    <Tab label="Live Monitoring" value="live-monitoring" />
                    <Tab label="Parameter Settings" value="parameter-settings" />
                  </TabList>
                </Box>

                <TabPanel value="info">
                  <PatientSectionDetails pagePatient={pagePatient} />
                </TabPanel>

                <TabPanel value="encounters">
                  <EncounterList patientID={patientID} />
                </TabPanel>

                <TabPanel value="remarks">
                  <PatientSectionRemarks pagePatient={pagePatient} />
                </TabPanel>

                <TabPanel value="monitoring-charts">
                  <PatientSectionMonitoringCharts patientID={patientID} pagePatient={pagePatient} />
                </TabPanel>

                <TabPanel value="live-monitoring" sx={{ backgroundColor: 'black', color: 'white' }}>
                  <Telemetry
                    Device={{ slot1: patientID, maxslot: 1 }}
                    columnSize={
                      call ? { xs: 12, sm: 12, md: 12, lg: 6, xl: 6 } : { xs: 12, sm: 6, md: 6, lg: 6, xl: 4 }
                    }
                    standalone
                  />
                </TabPanel>

                <TabPanel value="parameter-settings">
                  <PatientSectionParameterSettings pagePatient={pagePatient} />
                </TabPanel>
              </TabContext>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
